import { Typography } from 'antd';
import React from 'react';

import type { ProjectHeaderProps } from '@/api/project-details/mocks/projectHome.types';
import { Pill, type PillTone } from '@/components/common/Pill';
import { HealthStatusBadge as HealthStatus } from '@/components/project_details_components/HealthStatusBadge';
import { TeamMemberPill } from '@/components/project_details_components/TeamMemberPill';
import { WarningIcon as WarningOutlined } from '@/icons';

const { Text } = Typography;

/** Length of the Wednesday-anchored submission cycle; `daysLate` reaches this on Wednesday. */
const CYCLE_LENGTH_DAYS = 7;

// The two fields are on different scales: daysLate is a 1-7 cycle position whose
// presence alone means the week is unsubmitted; missingUpdateDays is unbounded.
function resolveMissingUpdate(
  daysLate: number | null | undefined,
  missingUpdateDays: number | undefined,
): { label: string; tone: PillTone } | null {
  if (daysLate !== undefined && daysLate !== null) {
    // Wednesday opens a new cycle, so a full 7 means the update is due today
    // rather than a week overdue — say so instead of counting days.
    if (daysLate >= CYCLE_LENGTH_DAYS) {
      return { label: 'Status update is Due', tone: 'warning' };
    }
    return {
      label: `Missing weekly update · ${daysLate} d`,
      tone: daysLate >= 5 ? 'error' : 'warning',
    };
  }
  // Legacy scale: unbounded days since the last update, so 7 here is genuine
  // staleness and must not be relabelled as "due".
  if (missingUpdateDays !== undefined && missingUpdateDays >= 7) {
    return {
      label: `Missing weekly update · ${missingUpdateDays} d`,
      tone: missingUpdateDays < 9 ? 'warning' : 'error',
    };
  }
  return null;
}

export function ProjectHeader({
  clientName,
  projectName,
  projectId,
  category,
  dealStage,
  healthStatus,
  dealOwner,
  missingUpdateDays,
  daysLate,
  teamMembers = undefined,
}: Readonly<ProjectHeaderProps>): React.ReactElement {
  const safeTeamMembers = teamMembers || [];
  const missingUpdate = resolveMissingUpdate(daysLate, missingUpdateDays);
  return (
    <div className="project-header">
      <div className="project-header__top-row">
        <div className="project-header__title-section">
          <div className="project-header__title-stack">
            <h4 className="project-header__client-name">{clientName}</h4>
            <h1 className="project-header__project-name">{projectName}</h1>
          </div>
        </div>
      </div>

      <div className="project-header__bottom-row">
        <div className="project-header__details">
          {projectId && (
            <div className="project-header__detail-row">
              <span className="project-header__detail-label">Project ID</span>
              <Text type="secondary">{`P-${projectId}`}</Text>
            </div>
          )}
          {category && (
            <div className="project-header__detail-row">
              <span className="project-header__detail-label">Category</span>
              <Text type="secondary">{category}</Text>
            </div>
          )}
          {dealStage && (
            <div className="project-header__detail-row">
              <span className="project-header__detail-label">Deal Stage</span>
              <Pill tone="success">{dealStage}</Pill>
            </div>
          )}
          {healthStatus && (
            <div className="project-header__detail-row project-header__detail-row--health">
              <span className="project-header__detail-label">Project Health</span>
              <HealthStatus status={healthStatus} />
              {missingUpdate && (
                <Pill tone={missingUpdate.tone} leading={<WarningOutlined />}>
                  {missingUpdate.label}
                </Pill>
              )}
            </div>
          )}
        </div>

        {(dealOwner || safeTeamMembers.length > 0) && (
          <div className="project-header__team-section">
            <span className="project-header__detail-label">Team Roles</span>
            <div className="project-header__team-members">
              {dealOwner &&
                (() => {
                  const nameParts = dealOwner.split(' ');
                  const initials =
                    nameParts.length >= 2
                      ? (nameParts[0].charAt(0) + nameParts[1].charAt(0)).toUpperCase()
                      : dealOwner.charAt(0).toUpperCase();
                  return (
                    <TeamMemberPill
                      key="deal-owner"
                      initials={initials}
                      role="DEAL OWNER"
                      name={dealOwner}
                    />
                  );
                })()}
              {(() => {
                const deliveryLeads = safeTeamMembers.filter(
                  (member) => member.role === 'DELIVERY_LEAD',
                );
                return deliveryLeads.slice(0, 1).map((member) => {
                  const nameParts = member.name.split(' ');
                  const initials =
                    member.avatar ||
                    (nameParts.length >= 2
                      ? (nameParts[0].charAt(0) + nameParts[1].charAt(0)).toUpperCase()
                      : member.name.charAt(0).toUpperCase());

                  return (
                    <TeamMemberPill
                      key={member.id}
                      initials={initials}
                      role={member.role}
                      name={member.name}
                      isActive={member.isActive}
                      lastWorkedDate={member.lastWorkedDate}
                    />
                  );
                });
              })()}
              {(() => {
                const pmMembers = safeTeamMembers.filter((member) => member.role === 'PM');
                return pmMembers.slice(0, 2).map((member, id) => {
                  const nameParts = member.name.split(' ');
                  const initials =
                    member.avatar ||
                    (nameParts.length >= 2
                      ? (nameParts[0].charAt(0) + nameParts[1].charAt(0)).toUpperCase()
                      : member.name.charAt(0).toUpperCase());

                  return (
                    <TeamMemberPill
                      key={member.id}
                      initials={initials}
                      role={member.role}
                      name={member.name}
                      count={id === 1 && pmMembers.length > 2 ? pmMembers.length - 2 : undefined}
                      isActive={member.isActive}
                      lastWorkedDate={member.lastWorkedDate}
                    />
                  );
                });
              })()}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
